import { useMemo, useState } from 'react';
import styled from 'astroturf/react';
import { TextButton } from 'js/design_system/TextButton';
import { Body } from 'js/design_system/Body';
import PaperThumbnail from './PaperThumbnail';
import WorkMetadata from './WorkMetadata';
import { Work, WorkClaim } from './types';
import trackClick from 'js/utils/trackClick';

const LIST_PAGE_SIZE = 5;

type RelatedPapersProps = {
  sourceClaim: WorkClaim;
  works: Work[];
};

const Card = styled.div`
  background: var(--background-white, #fff);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--corner-radius-radius-lg, 16px);
  overflow: hidden;
`;

const ListCard = styled(Card)`
  padding: var(--spacing-sm-32, 32px);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-32, 32px);
`;

const RelatedPaperRow = styled.div`
  display: flex;
  gap: var(--spacing-sm-24, 24px);
  align-items: flex-start;
  width: 100%;
`;

const RightRailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
`;

const ShowMoreButton = styled(TextButton)`
  color: var(--text-primary, #141413);
  font-weight: var(--type-font-weight-regular);
  gap: var(--spacing-xs-4, 4px);
`;

const ShowMoreButtonText = styled.span`
  text-decoration: underline;
`;

const ShowMoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const SourceCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
`;

const RelatedPapers: React.FunctionComponent<RelatedPapersProps> = ({
  sourceClaim,
  works,
}) => {
  const [expanded, setExpanded] = useState(false);
  const visible = useMemo(
    () => (expanded ? works : works.slice(0, LIST_PAGE_SIZE)),
    [works, expanded],
  );
  const showMore = works.length > LIST_PAGE_SIZE && !expanded;

  return (
    <RightRailSection>
      <Body bold size="lg" as="h2">
        Related papers
      </Body>
      <ListCard>
        {visible.map((work, i) => (
          <RelatedPaperRow key={`${work.url}-${i}`}>
            <SourceCopy>
              <WorkMetadata
                sourceClaim={sourceClaim}
                work={work}
                metadataLocation="related_papers"
              />
            </SourceCopy>
            <PaperThumbnail work={work} paperType="related_paper" />
          </RelatedPaperRow>
        ))}
        {showMore ? (
          <ShowMoreRow>
            <ShowMoreButton
              type="button"
              inline
              iconRight="expand_more"
              onClick={() => {
                trackClick({
                  source: 'single_claim_page.related_papers.show_more',
                  extra: {
                    source_claim_id: sourceClaim.id,
                  },
                });
                setExpanded(true);
              }}
            >
              <ShowMoreButtonText>Show more</ShowMoreButtonText>
            </ShowMoreButton>
          </ShowMoreRow>
        ) : null}
      </ListCard>
    </RightRailSection>
  );
};

export default RelatedPapers;
